import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../../lib/api";
import { useCurrentCompany } from "../components/CompanySwitcher";
import { fmtMoney, fmtDate } from "../../../lib/format";

// Invoices an import flagged for a human to look at. Two reasons:
//   - they dropped off the QBO export (often because someone closed them in QuickBooks), or
//   - QuickBooks now marks them 'accepted' — the customer took the quote, which usually means
//     it's about to be billed, but not that the money arrived.
// Either way they stay open here until the user confirms Paid or keeps them open.
interface FlaggedInvoice {
  id: number;
  number: string;
  issue_date: string;
  amount: number;
  memo: string | null;
  status: string;              // QBO status text, e.g. 'open' / 'accepted' / 'Pending'
  collection_status: "open" | "ignored";
  close_suggested_at: string | null;
  client_name: string;
}

const isAccepted = (s: string | null | undefined) => /^accepted$/i.test((s || "").trim());

export default function Review() {
  const { current } = useCurrentCompany();
  const [rows, setRows] = useState<FlaggedInvoice[] | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const currency = current?.currency || "THB";

  async function load() {
    if (!current) return;
    setRows(null);
    const r = await api<{ estimates: FlaggedInvoice[] }>(
      `/api/collection/estimates?company_id=${current.id}&review=1`,
    );
    setRows(r.estimates);
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [current?.id]);

  // "keep" only dismisses the flag and leaves the invoice as it is; the others close it out.
  async function resolve(id: number, action: "paid" | "written_off" | "keep") {
    setBusyId(id);
    try {
      await api(`/api/collection/estimates/${id}`, {
        method: "PATCH",
        body: JSON.stringify(action === "keep" ? { close_suggested: false } : { collection_status: action }),
      });
      // Drop the row locally so the list feels instant; it no longer matches the review filter.
      setRows((prev) => (prev ? prev.filter((r) => r.id !== id) : prev));
      // Nudge the sidebar badge to refetch its count.
      window.dispatchEvent(new Event("pc-review-changed"));
    } catch (e) {
      alert(e instanceof Error ? e.message : "Could not update the invoice.");
    } finally {
      setBusyId(null);
    }
  }

  const total = (rows || []).reduce((s, r) => s + r.amount, 0);
  const acceptedCount = (rows || []).filter((r) => isAccepted(r.status)).length;
  const missingCount = (rows || []).length - acceptedCount;

  return (
    <div className="space-y-4">
      <div className="card p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-base font-semibold">Review — needs a decision</div>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">
              An import flagged these, either because they were <strong>not in the file</strong> (likely closed in
              QuickBooks) or because QuickBooks now marks them <strong>accepted</strong> — the customer took the
              quote, which isn't the same as being paid. Nothing here has been closed: each one keeps its current
              status until you decide. Mark <strong>Paid</strong> (or <strong>Write off</strong>, for a quotation)
              to close it, or <strong>Keep</strong> to dismiss the flag and leave it alone — dismissing an accepted
              one sticks, later imports won't raise it again.
            </p>
            {rows && rows.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2 text-xs">
                {acceptedCount > 0 && (
                  <span className="rounded bg-amber-50 px-2 py-1 font-medium text-amber-800 ring-1 ring-amber-200">
                    {acceptedCount} accepted in QuickBooks
                  </span>
                )}
                {missingCount > 0 && (
                  <span className="rounded bg-slate-100 px-2 py-1 font-medium text-slate-700 ring-1 ring-slate-200">
                    {missingCount} not in the last import
                  </span>
                )}
              </div>
            )}
          </div>
          {rows && rows.length > 0 && (
            <div className="shrink-0 rounded-md bg-amber-50 px-3 py-2 text-right ring-1 ring-amber-200">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">To review</div>
              <div className="text-lg font-semibold text-amber-900">{rows.length}</div>
              <div className="text-xs text-amber-700">{fmtMoney(total, currency)}</div>
            </div>
          )}
        </div>
      </div>

      {rows === null && <div className="text-sm text-slate-500">Loading…</div>}

      {rows !== null && rows.length === 0 && (
        <div className="card p-8 text-center">
          <div className="text-2xl">✅</div>
          <div className="mt-2 font-semibold text-slate-900">Nothing to review</div>
          <div className="mt-1 text-sm text-slate-500">
            Flagged invoices show up here after an <Link to="/collection/import" className="underline">import</Link>.
          </div>
        </div>
      )}

      {rows !== null && rows.length > 0 && (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-4 py-2">Client</th>
                <th className="px-2 py-2">Number</th>
                <th className="px-2 py-2">Issued</th>
                <th className="px-2 py-2 text-right">Amount</th>
                <th className="px-2 py-2">Why</th>
                <th className="px-2 py-2">Flagged</th>
                <th className="px-2 py-2">Memo</th>
                <th className="px-4 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium text-slate-900">{r.client_name}</td>
                  <td className="px-2 py-2 font-mono text-xs">
                    {r.number}
                    {r.collection_status === "ignored" && (
                      <span className="ml-1.5 rounded bg-slate-100 px-1 py-0.5 font-sans text-[10px] font-medium text-slate-600"
                            title="Marked Quotation only — not chased as debt.">
                        quote
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-slate-600">{fmtDate(r.issue_date)}</td>
                  <td className="px-2 py-2 text-right font-mono">{fmtMoney(r.amount, currency)}</td>
                  <td className="px-2 py-2">
                    {isAccepted(r.status)
                      ? <span className="whitespace-nowrap rounded bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 ring-1 ring-amber-200"
                              title="QuickBooks marks this estimate as accepted — billed or about to be, but not necessarily paid.">
                          Accepted in QBO
                        </span>
                      : <span className="whitespace-nowrap rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
                              title="This invoice was not in the latest import file.">
                          Not in last import
                        </span>}
                  </td>
                  <td className="px-2 py-2 text-xs text-slate-500">
                    {r.close_suggested_at ? fmtDate(r.close_suggested_at.slice(0, 10)) : "—"}
                  </td>
                  <td className="max-w-xs truncate px-2 py-2 text-xs text-slate-500">{r.memo}</td>
                  <td className="px-4 py-2">
                    {/* A quotation was never debt, so "Paid" doesn't apply — write it off instead. */}
                    <div className="flex justify-end gap-2">
                      {r.collection_status === "ignored" ? (
                        <button
                          type="button"
                          className="btn-primary !py-1 !px-3 text-xs"
                          disabled={busyId === r.id}
                          title="Close this quotation for good — it leaves the book and every total."
                          onClick={() => resolve(r.id, "written_off")}
                        >
                          Write off
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn-primary !py-1 !px-3 text-xs"
                          disabled={busyId === r.id}
                          title="Confirm the money arrived — closes the invoice."
                          onClick={() => resolve(r.id, "paid")}
                        >
                          Paid
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn-secondary !py-1 !px-3 text-xs"
                        disabled={busyId === r.id}
                        title="Dismiss the flag and leave the invoice exactly as it is."
                        onClick={() => resolve(r.id, "keep")}
                      >
                        {r.collection_status === "ignored" ? "Keep as quote" : "Keep open"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
